import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

// Third-parties.
import dayjs from "dayjs";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";

// Context
import { InstanceContext } from "../../../context/InstanceContext";
import { useLicences } from "../../../context/LicenceContext";

// Types
import { ActivityHashTimeSlice, ActivityHashData } from "./types";
import { InstanceOptions, ActivityResultsTableProps } from "./types";

// Helpers
import Helper from "../../../helpers/Helper";
import {
    archiverUrl,
    fetchResults,
    fetchWithAuthorization, getAggregateInt, getTimeZone,
} from "../../../helpers/utils";
import { showError } from '../../../utils/showAlert';
import ConditionalRender from "../../../helpers/ConditionalRender";
import useChartOptions from "../../../helpers/useChartOptions";

// Constants
import { CHART_DATA_TYPE } from "../../../component/Constants";

const UPMOST_CHART_INDEX_VALUE = 99999

const ActivityComparisonStatementsChart = (props: ActivityResultsTableProps) => {
    const { instances } = useContext(InstanceContext);
    const { licences } = useLicences();

    const [loading, setLoading] = useState(false)
    const [statements, setStatements] = useState<ActivityHashData[][]>([]);
    const [topNChartData, setTopNChartData] = useState<{
        [instanceIndex: string | number]: { [key: string]: { values: ActivityHashTimeSlice[], orderIndex: number } }
    }>({});

    const timezone = getTimeZone()

    const chart1Ref = useRef(null);
    const chart2Ref = useRef(null);

    const aggregateInt = useMemo(() => getAggregateInt(props.options.aggregate, props.instanceOptions, instances), [props.options.aggregate])

    const __getOtherTotal = (allMetrics: any, topNMetrics: any): ActivityHashTimeSlice => {
        let totalValue = allMetrics.waittime
        topNMetrics.forEach((item: any) => {
            const getTopNItemAtTimeSlice = item.find((x: ActivityHashTimeSlice) => x.timeslice === allMetrics.timeslice) || 0
            totalValue = getTopNItemAtTimeSlice.waittime ? totalValue - getTopNItemAtTimeSlice.waittime : totalValue
        })
        return {
            timeslice: allMetrics.timeslice,
            waittime: totalValue < 0 ? 0 : totalValue
        }
    }

    const getInstanceResults = useCallback(async (instanceOption: InstanceOptions) => {
        try {
            let chartApiDataResults: ActivityHashData[][] = [[]]
            const apiTagString = `${archiverUrl(1)}/activity/sql?`
            const from = dayjs(instanceOption.periodStart).format('YYYY-MM-DD+HH:mm')
            const to = dayjs(instanceOption.periodEnd).format('YYYY-MM-DD+HH:mm')
            let customIdApiString = ''
            if (props.options.customSqlId) {
                customIdApiString = props.options.showSqlId ? `&sqlid=${props.options.customSqlId}` : `&sqlhash=${props.options.customSqlId}`
            }
            chartApiDataResults = await fetchResults([fetchWithAuthorization(`${apiTagString}from=${from}&to=${to}&tz=${timezone}&id=${instanceOption.instanceId}&interval=${aggregateInt}&statistic=executions&sort=waittime+desc&limit=${props.options.topN}&type=${instanceOption.instanceType}&${customIdApiString}`)])

            let topNChartApiDataResults: ActivityHashData[] = []
            topNChartApiDataResults = chartApiDataResults[0].slice(0, props.options.topN)

            let topNChartTimePromises = []
            for (let sqlHashData of topNChartApiDataResults) {
                const apiSqlHash = `sqlhash=${sqlHashData.sqlhash}`
                topNChartTimePromises.push(fetchWithAuthorization(`${archiverUrl(1)}/activity/time?from=${from}&to=${to}&tz=${timezone}&id=${instanceOption.instanceId}&interval=${aggregateInt}&statistic=executions&sort=waittime+desc&type=${instanceOption.instanceType}&${apiSqlHash}`))
            }

            const topNChartTimeResults = await fetchResults(topNChartTimePromises)

            // sort by time
            topNChartTimeResults.forEach(chartTime => {
                chartTime.sort(function compare(a: ActivityHashTimeSlice, b: ActivityHashTimeSlice) {
                    const aa: number = dayjs(a.timeslice).valueOf();
                    const bb: number = dayjs(b.timeslice).valueOf();
                    return aa < bb ? -1 : 1;
                })
            })

            let allChartTimeResults: ActivityHashTimeSlice[][] = []

            if (props.options.other) {
                allChartTimeResults = await fetchResults([fetchWithAuthorization(`${archiverUrl(1)}/activity/time?from=${from}&to=${to}&tz=${timezone}&id=${instanceOption.instanceId}&interval=${aggregateInt}&statistic=executions&sort=waittime+desc&type=${instanceOption.instanceType}&limit=50000`)])

                // sort by time
                allChartTimeResults[0].sort(function compare(a: any, b: any) {
                    const aa: number = dayjs(a.timeslice).valueOf();
                    const bb: number = dayjs(b.timeslice).valueOf();
                    return aa - bb;
                })
            }

            const topNData: { [key: string]: { values: ActivityHashTimeSlice[], orderIndex: number } } = {}

            topNChartTimeResults.forEach((result: ActivityHashTimeSlice[], index: number) => {
                const sqlhash = props.options.showSqlId ? topNChartApiDataResults[index].sqlid : topNChartApiDataResults[index].sqlhash

                if (sqlhash) {
                    topNData[sqlhash] = {
                        values: result.map((item: any) => {
                            return {
                                timeslice: item.timeslice,
                                waittime: item.waittime,
                            }
                        }),
                        orderIndex: index + 1
                    }
                }
                if (props.options.other) {
                    const otherValues = result.map((item: ActivityHashTimeSlice) => {
                        const getAllAtTimeSlice = allChartTimeResults[0].find((otherItem: ActivityHashTimeSlice) => otherItem.timeslice === item.timeslice)
                        if (getAllAtTimeSlice) {
                            const otherTotal = __getOtherTotal(getAllAtTimeSlice, topNChartTimeResults)
                            const valuesExist = topNData['Other SQL']?.values?.find(item => item.timeslice === otherTotal.timeslice)
                            return valuesExist || otherTotal
                        }
                        return []
                    })
                    topNData['Other SQL'] = {
                        // @ts-ignore
                        values: topNData['Other SQL']?.values || otherValues,
                        orderIndex: UPMOST_CHART_INDEX_VALUE
                    }
                }
            })

            return {
                statements: chartApiDataResults[0],
                topNData: topNData
            }
        } catch (error: any) {
            console.error(error)
            const errorMessage = error.message || error.toString();
            showError('Failed to load activity comparison statements chart', errorMessage);
        }
    }, [topNChartData, props.generateId])

    useEffect(() => {
        const start = async () => {
            try {
                setStatements([]);
                setLoading(true)
                const promiseArray: any[] = []
                const dataToSet: any = {}
                const statementsData: any[] = [];
                const topNData: any[] = [];

                props.instanceOptions.forEach((instanceOption: InstanceOptions) => {
                    promiseArray.push(getInstanceResults(instanceOption))
                })

                const results = await Promise.all(promiseArray)

                results.forEach((result, index: number) => {
                    if (result) {
                        statementsData.push(result.statements);
                        topNData.push(result.topNData);

                        dataToSet[index] = result.topNData || {};
                    }
                });

                setStatements(statementsData)
                setTopNChartData(dataToSet)
                setLoading(false)
            } catch (error: any) {
                console.error('Failed to start activity comparison statements chart:', error);
                const errorMessage = error.message || error.toString();
                showError('Failed to load activity comparison statements chart data', errorMessage);
                setLoading(false);
                setStatements([]);
                setTopNChartData({});
            }
        }
        void start()
    }, [props.generateId])

    useEffect(() => {
        if (statements.length && props.updateStatements) {
            props.updateStatements(statements)
        }
    }, [statements])

    const chartOptions = useChartOptions(
            topNChartData,
            props.instanceOptions,
            {
                metric: 'waittime',
                tooltip: {
                    formatter: function () {
                        return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.TIME);
                    },
                },
                chart: props.options.chart,
                interval: aggregateInt,
                height: `300px`,
                combinedChanges: props.combinedChanges,
                licences: licences
            },
            props.generateId,
            props.setStatementColors
    );

    return <div className="card">
        <div className="card-header">
            <ConditionalRender if={loading}>
                <div className="loader"
                     style={{ borderWidth: 0, position: "absolute", width: '80%', margin: 'auto', height: "5px" }}>
                    <div className="bar"/>
                </div>
            </ConditionalRender>
            <i className="fal fa-scroll fa-fw" aria-hidden="true"/>
            Top statements over time
            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
               data-bs-target="#collapseStatementsChart" aria-expanded="false"
               aria-controls="collapseStatementsChart"/>
        </div>
        <div id="collapseStatementsChart" className="card-body collapse show combined-charts pt-5">
            <ConditionalRender if={chartOptions[0]}>
                <HighchartsReact ref={chart1Ref} constructorType={"chart"} highcharts={Highcharts}
                                 options={chartOptions[0]}/>
            </ConditionalRender>
            <div className={"separator"}/>
            <ConditionalRender if={chartOptions[1]}>
                <HighchartsReact ref={chart2Ref} constructorType={"chart"} highcharts={Highcharts}
                                 options={chartOptions[1]}/>
            </ConditionalRender>
        </div>
    </div>
}

export default ActivityComparisonStatementsChart;
